import { Avatar, Box, Button, DataTable, EmptyState, Flex, LoadError, NoResults, SegmentedControl, Text } from "@dehub/theme";
import type { DataTableColumn } from "@dehub/theme";
import { PencilSimpleIcon, TrashIcon, UserCirclePlusIcon, UsersFourIcon, UsersIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { WarehouseStaffApi } from "@/apis";
import type { IWarehouseStaffRole } from "@/apis/warehouseStaff";
import bz from "@/features/business/business.module.css";
import classes from "@/features/warehouse/warehouse.module.css";
import type { IWarehouseStaffRow } from "@/models/Warehouse";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { RowAction } from "@/shared/a11y/row-action";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { Dialog } from "@/utils/confirm-modal";
import HttpHandler from "@/utils/http-handler";
import { message } from "@/utils/message";
import { renderName } from "@/utils/render-name";
import { useWarehouseStaffPermissions } from "../hooks/useWarehouseStaffPermissions";
import { WAREHOUSE_STAFF_ROLES, getRoleLabel } from "../constants";
import { AddStaffModal } from "./AddStaffModal";
import { BulkAddStaffModal } from "./BulkAddStaffModal";

interface IListResponse {
  count: number;
  rows: IWarehouseStaffRow[];
}

interface Props {
  warehouseId: string;
  readOnly?: boolean;
  /** Called with the row count after every load, so the page's tab badge follows adds and removals. */
  onCountChange?: (count: number) => void;
}

export function WarehouseStaffTab({ warehouseId, readOnly = false, onCountChange }: Props) {
  const { t } = useTranslation("staff");
  const { t: tCommon } = useTranslation("common");
  const perms = useWarehouseStaffPermissions(warehouseId);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rows, setRows] = useState<IWarehouseStaffRow[]>([]);

  useEffect(() => {
    if (!loading && !loadError) onCountChange?.(rows.length);
  }, [loading, loadError, rows.length, onCountChange]);
  const [roleFilter, setRoleFilter] = useState<IWarehouseStaffRole | "ALL">("ALL");
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<IWarehouseStaffRow | null>(null);

  const fetchList = useCallback(async () => {
    if (!perms.list.hasAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      // The whole list is loaded and filtered here, so the role tabs can show their counts.
      const res = (await WarehouseStaffApi.list(warehouseId, { offset: { page: 1, limit: 200 } })) as IListResponse;
      setRows(res?.rows ?? []);
    } catch (err) {
      const text = (err as HttpHandler)?.message ?? t("msg.staffListError");
      setLoadError(text);
      message.error(text);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, perms.list.hasAccess]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const canAdd = !readOnly && (perms.addKeeper.hasAccess || perms.addSub.hasAccess || perms.isKeeperWithDelegation);
  const canBulk = !readOnly && perms.isManager && perms.bulk.hasAccess;
  const addReason: ReactNode = canAdd ? null : <MissingPermission code="PRT_WRH_STAFF_ADD_KEEPER, PRT_WRH_STAFF_ADD_SUB" />;
  const bulkReason: ReactNode = canBulk ? null : <MissingPermission code="PRT_WRH_STAFF_BULK" />;

  const handleRemove = (row: IWarehouseStaffRow) => {
    Dialog.confirm(t("warehouse.removeConfirm"), async (key) => {
      if (key !== "confirm") return;
      try {
        await WarehouseStaffApi.remove(warehouseId, row.id);
        message.success(t("msg.removeSuccess"));
        await fetchList();
        await perms.refreshContext();
      } catch (err) {
        message.error((err as HttpHandler)?.message ?? t("msg.removeError"));
      }
    });
  };

  // A manager edits every row; a keeper with the delegation flag edits everyone except keepers.
  const canEditRow = (row: IWarehouseStaffRow): boolean => {
    if (readOnly) return false;
    if (perms.isManager) return true;
    return perms.isKeeperWithDelegation && row.staffRole !== "WAREHOUSE_KEEPER";
  };

  const counts = useMemo(() => {
    const map: Record<string, number> = { ALL: rows.length };
    rows.forEach((r) => {
      map[r.staffRole] = (map[r.staffRole] ?? 0) + 1;
    });
    return map;
  }, [rows]);

  const displayed = useMemo(() => (roleFilter === "ALL" ? rows : rows.filter((r) => r.staffRole === roleFilter)), [rows, roleFilter]);

  const columns: DataTableColumn<IWarehouseStaffRow>[] = [
    {
      key: "actions",
      header: t("warehouse.colAction"),
      width: 92,
      render: (row) => {
        const rowAllowed = canEditRow(row);
        const rowReason = (code: string): ReactNode => (rowAllowed ? <MissingPermission code={code} /> : t("warehouse.noRowRights"));
        return (
          <Box className={bz.rowActions}>
            <RowAction
              label={t("warehouse.editTooltip")}
              reason={rowAllowed && perms.edit.hasAccess ? null : rowReason("PRT_WRH_STAFF_EDIT")}
              icon={PencilSimpleIcon}
              anchor={`${row.id}:edit`}
              onClick={() => setEditTarget(row)}
            />
            <RowAction
              label={t("warehouse.removeTooltip")}
              reason={rowAllowed && perms.remove.hasAccess ? null : rowReason("PRT_WRH_STAFF_REMOVE")}
              icon={TrashIcon}
              anchor={`${row.id}:remove`}
              onClick={() => handleRemove(row)}
            />
          </Box>
        );
      },
    },
    {
      key: "user",
      header: t("warehouse.colStaff"),
      render: (row) => (
        <Box className={classes.keeper}>
          <Avatar src={row.user?.avatar || undefined} size={26} radius="xl" />
          <Box className={bz.bizText}>
            <Text component="span" className={bz.bizName}>
              {renderName(row.user)}
            </Text>
            {row.user?.phone ? (
              <Text component="span" className={bz.mono}>
                {row.user.phone}
              </Text>
            ) : null}
          </Box>
        </Box>
      ),
    },
    { key: "role", header: t("warehouse.colWarehouseRole"), render: (row) => <Text component="span" fz={14}>{getRoleLabel(t, row.staffRole)}</Text> },
    {
      key: "scope",
      header: t("warehouse.colScope"),
      width: 120,
      render: (row) => <Text component="span" fz={14}>{row.scope === "MULTI" ? t("warehouse.scopeMulti") : t("warehouse.scopeSingle")}</Text>,
    },
    {
      key: "special",
      header: t("warehouse.colSpecial"),
      render: (row) =>
        row.isPrimary || row.canManageStaff ? (
          <Box className={classes.chips}>
            {row.isPrimary ? <Box component="span" className={classes.chip}>{t("warehouse.primary")}</Box> : null}
            {row.canManageStaff ? <Box component="span" className={classes.chip}>{t("warehouse.manageBadge")}</Box> : null}
          </Box>
        ) : (
          <Text component="span" className={bz.muted}>
            -
          </Text>
        ),
    },
  ];

  if (!perms.list.hasAccess && !perms.isStaffAtWarehouse) {
    return <Box>{perms.list.accessDenied()}</Box>;
  }

  const emptyState = loadError ? (
    <LoadError title={t("warehouse.loadErrorTitle")} message={loadError} retryLabel={tCommon("actions.retry")} onRetry={fetchList} />
  ) : roleFilter !== "ALL" ? (
    <NoResults title={t("warehouse.noResultsTitle")} description={t("warehouse.noResultsText")} onClear={() => setRoleFilter("ALL")} clearLabel={t("warehouse.showAll")} />
  ) : (
    <EmptyState
      icon={<UsersIcon size={24} aria-hidden="true" />}
      title={t("warehouse.empty")}
      description={t("warehouse.emptyText")}
      action={
        <ReasonTooltip reason={addReason}>
          <Button variant="default" onClick={() => setAddOpen(true)}>
            {t("warehouse.addBtn")}
          </Button>
        </ReasonTooltip>
      }
    />
  );

  return (
    <Box pos="relative" mih={300}>
      <Flex justify="space-between" align="center" wrap="wrap" gap="md" mb="md">
        <SegmentedControl
          aria-label={t("warehouse.roleFilter")}
          value={roleFilter}
          onChange={(value) => setRoleFilter(value as IWarehouseStaffRole | "ALL")}
          classNames={{ root: bz.segmentRoot, indicator: bz.segmentIndicator, label: bz.segmentLabel }}
          data={[
            { value: "ALL", label: `${t("warehouse.allShort")} ${counts.ALL ?? 0}` },
            ...WAREHOUSE_STAFF_ROLES.map((r) => ({ value: r.code, label: `${getRoleLabel(t, r.code)} ${counts[r.code] ?? 0}` })),
          ]}
        />
        <Flex gap="sm" align="center">
          <ReasonTooltip reason={bulkReason}>
            <Button variant="default" leftSection={<UsersFourIcon size={16} />} onClick={() => setBulkOpen(true)}>
              {t("warehouse.bulkBtn")}
            </Button>
          </ReasonTooltip>
          <ReasonTooltip reason={addReason}>
            <Button leftSection={<UserCirclePlusIcon size={16} />} onClick={() => setAddOpen(true)}>
              {t("warehouse.addBtn")}
            </Button>
          </ReasonTooltip>
        </Flex>
      </Flex>

      <Box className={bz.tableWrap}>
        <DataTable<IWarehouseStaffRow>
          data={displayed}
          rowKey={(r) => r.id}
          cardPadding={0}
          columns={columns}
          loading={loading}
          emptyState={emptyState}
          tableProps={{ striped: false, highlightOnHover: true, verticalSpacing: "sm", stickyHeader: false }}
        />
      </Box>

      <AddStaffModal
        opened={addOpen || editTarget !== null}
        warehouseId={warehouseId}
        target={editTarget}
        onClose={() => {
          setAddOpen(false);
          setEditTarget(null);
        }}
        onSuccess={async () => {
          setAddOpen(false);
          setEditTarget(null);
          await fetchList();
          await perms.refreshContext();
        }}
      />

      <BulkAddStaffModal
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={async () => {
          setBulkOpen(false);
          await fetchList();
          await perms.refreshContext();
        }}
      />
    </Box>
  );
}
